import { pino } from 'pino'

import { HttpCallSchedulerErrorData } from './http/http.call.scheduler'

const logger = pino({
  level: process.env.LOG_LEVEL || 'info',
  name: 'error-deserializer',
})

/**
 * The type of expected application errors
 */
export enum ApplicationErrorName {
  INVALID_ARGUMENT = 'invalid_argument',
  PERMISSION_ERROR = 'permission_error',
  NOT_FOUND_ERROR = 'not_found_error',
  STATE_ERROR = 'state_error',
  MISSING_VALUE = 'missing_value',
  INTERNAL_SERVER_ERROR = 'internal_server_error',
  THIRD_PARTY_ERROR = 'third_party_error',
}

const EXPECTED_CLIENT_ERROR_TYPES: ReadonlySet<string> = new Set([
  ApplicationErrorName.INVALID_ARGUMENT,
  ApplicationErrorName.PERMISSION_ERROR,
  ApplicationErrorName.NOT_FOUND_ERROR,
  ApplicationErrorName.MISSING_VALUE,
])

/**
 * Expected 4xx outcomes (bad input, missing resource, no permission, SCA challenges).
 * ApplicationErrorHandler and RPC spans skip Sentry capture for these. STATE_ERROR and THIRD_PARTY_ERROR stay reported.
 */
export const isExpectedClientError = (type: string): boolean => EXPECTED_CLIENT_ERROR_TYPES.has(type)

/**
 * The HTTP status codes for the application error types
 */
const CODES_FOR: Record<ApplicationErrorName, { code: number; statusText: string }> = {
  /**
   * The request was invalid or cannot be otherwise served.
   */
  [ApplicationErrorName.INVALID_ARGUMENT]: { code: 400, statusText: 'Bad Request' },
  /**
   * The request was forbidden.
   */
  [ApplicationErrorName.PERMISSION_ERROR]: { code: 403, statusText: 'Forbidden' },
  [ApplicationErrorName.NOT_FOUND_ERROR]: { code: 404, statusText: 'Not Found' },
  /**
   * The request could not be processed because of conflict in the current state of the resource.
   */

  [ApplicationErrorName.STATE_ERROR]: { code: 409, statusText: 'Conflict' },
  /**
   * The server understood the request but cannot process it.
   */
  [ApplicationErrorName.MISSING_VALUE]: { code: 422, statusText: 'Unprocessable Entity' },
  /**
   * The server encountered an unexpected condition that prevented it from fulfilling the request.
   */
  [ApplicationErrorName.INTERNAL_SERVER_ERROR]: { code: 500, statusText: 'Internal Server Error' },
  /**
   * The server was acting as a gateway or proxy to a third party service and received an invalid response from the upstream server.
   */
  [ApplicationErrorName.THIRD_PARTY_ERROR]: { code: 502, statusText: 'Bad Gateway' },
}

type _ApplicationError = {
  message: string
  type: ApplicationErrorName
  // meant to be used on the backend only
  info?: Record<string, unknown>
  // additional info to be sent to the client
  clientInfo?: Record<string, unknown>
}

/**
 * A custom error class that works well in an RPC context.
 * Uses the message and name to identify the error which are serializable fields.
 */
export class ApplicationError extends Error {
  /**
   * @param message the error message, this must be informative but generic since it will be
   * returned to the client
   * @param details the error details, this will be used to identify the error and provide
   * more information to the client. Any of the info fields are for internal use only (logging, etc).
   */
  constructor(
    message: string,
    details: { type: ApplicationErrorName; info?: Record<string, unknown>; clientInfo?: Record<string, unknown> },
  ) {
    super(JSON.stringify({ message, ...details }))
  }

  getDetails() {
    const { message, ...details }: _ApplicationError = JSON.parse(this.message)
    return details
  }
}

export const MissingValueError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.MISSING_VALUE, info })

export const ThirdPartyError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.THIRD_PARTY_ERROR, info })

export const InvalidArgumentError = (message: string, info?: Record<string, unknown>, clientInfo?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.INVALID_ARGUMENT, info, clientInfo })

export const NotFoundError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.NOT_FOUND_ERROR, info })

export const StateError = (message: string, info?: Record<string, unknown>, clientInfo?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.STATE_ERROR, info, clientInfo })

const FOREIGN_KEY_VIOLATION_MESSAGE = 'Foreign key constraint violated'

export const ForeignKeyViolationError = (info?: Record<string, unknown>) =>
  StateError(FOREIGN_KEY_VIOLATION_MESSAGE, info)

/**
 * Recognises a ForeignKeyViolationError on either side of an RPC hop, where it arrives as a plain Error.
 */
export const isForeignKeyViolation = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false
  }
  try {
    const { message, type }: _ApplicationError = JSON.parse(error.message)
    return type === ApplicationErrorName.STATE_ERROR && message === FOREIGN_KEY_VIOLATION_MESSAGE
  } catch {
    return false
  }
}

export const PermissionError = (message: string, info?: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.PERMISSION_ERROR, info })

export const SCARequiredError = (scaSessionCode: string) =>
  new ApplicationError('SCA required', { type: ApplicationErrorName.PERMISSION_ERROR, clientInfo: { scaSessionCode } })

export const SCAInvalidError = () =>
  new ApplicationError('SCA token invalid', { type: ApplicationErrorName.PERMISSION_ERROR })

export const PipelineTriggerError = (message: string, clientInfo: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.STATE_ERROR, clientInfo })

export const HttpCallSchedulerError = (message: string, info: Record<string, unknown>) =>
  new ApplicationError(message, { type: ApplicationErrorName.THIRD_PARTY_ERROR, info })

export const ErrorDeserializer = (error: Error): ApplicationError => {
  try {
    // always attempt at parsing the message as an application error
    const { message, type, info, clientInfo }: _ApplicationError = JSON.parse(error.message)
    return new ApplicationError(message, { type, info, clientInfo })
  } catch (e) {
    // if the message parsing fails, return an internal server error
    logger.error('Unknown error encountered', { error: error.message })
    return new ApplicationError('Internal Server Error', {
      type: ApplicationErrorName.INTERNAL_SERVER_ERROR,
      info: { error: error.message },
    })
  }
}

export const HttpCallSchedulerErrorDataFrom = (error: Error): HttpCallSchedulerErrorData => {
  const deserialized = ErrorDeserializer(error)
  const details = deserialized.getDetails()
  return details.info as HttpCallSchedulerErrorData
}

export type ResponseError = {
  message: string
  statusCode: number
  statusText: string
  // anything to bubble up to the client
  info?: Record<string, unknown>
}

const ResponseErrorFrom = (applicationError: ApplicationError): ResponseError => {
  const appError: _ApplicationError = JSON.parse(applicationError.message)
  return {
    message: appError.message,
    statusCode: CODES_FOR[appError.type].code,
    statusText: CODES_FOR[appError.type].statusText,
    info: appError.clientInfo,
  }
}

export const ResponseFrom = (applicationError: ApplicationError): Response => {
  const responseError = ResponseErrorFrom(applicationError)
  return new Response(JSON.stringify(responseError), { status: responseError.statusCode })
}

export function assertNotNull(value: unknown, message?: string): asserts value is NonNullable<typeof value> {
  if (value === null || value === undefined) {
    throw InvalidArgumentError(message ?? `Expected value to be defined, but received ${String(value)}`)
  }
}

export function assertFound(value: unknown, message?: string): asserts value is NonNullable<typeof value> {
  if (value === null || value === undefined) {
    throw NotFoundError(message ?? `Expected value to be defined, but received ${String(value)}`)
  }
}

export function assertAllowed(predicate: boolean, message?: string): asserts predicate {
  if (!predicate) {
    throw PermissionError(message ?? `Expected value to be allowed, but received ${String(predicate)}`)
  }
}

export function assertValid(predicate: boolean, message?: string): asserts predicate is true {
  if (!predicate) {
    throw InvalidArgumentError(message ?? `Expected predicate to be true, but received ${String(predicate)}`)
  }
}

export class PipelineStageError extends Error {
  readonly stageName: string

  constructor(stageName: string, cause: unknown) {
    const message = cause instanceof Error ? cause.message : String(cause)
    super(`Stage "${stageName}" failed: ${message}`, { cause })
    this.stageName = stageName
  }
}

/**
 * Convert an error into a plain object suitable for JSON.stringify. Native
 * Error fields (message, name, stack) are non-enumerable, so a raw Error
 * stringifies to `{}` — pipelines that emit errors into run-step `data.details`
 * for the lighthouse UI MUST run them through this first.
 *
 * Unwraps ApplicationError's JSON-encoded message into structured fields and
 * recurses through PipelineStageError's `cause` so the underlying error isn't
 * buried behind the wrapper.
 */
export const serializePipelineError = (error: unknown): Record<string, unknown> => {
  if (!(error instanceof Error)) {
    return { value: String(error) }
  }

  if (error instanceof PipelineStageError) {
    return {
      name: error.name,
      stageName: error.stageName,
      message: error.message,
      ...(error.cause !== undefined ? { cause: serializePipelineError(error.cause) } : {}),
      stack: error.stack,
    }
  }

  if (error instanceof ApplicationError) {
    try {
      const parsed: _ApplicationError = JSON.parse(error.message)
      return {
        name: error.name,
        message: parsed.message,
        type: parsed.type,
        ...(parsed.info ? { info: parsed.info } : {}),
        ...(parsed.clientInfo ? { clientInfo: parsed.clientInfo } : {}),
        stack: error.stack,
      }
    } catch {
      // Message wasn't a valid ApplicationError envelope — fall through to the generic Error case.
    }
  }

  return {
    name: error.name,
    message: error.message,
    stack: error.stack,
  }
}

/**
 * Error type definition for cloudflare containers
 */
export class ContainerMethodError extends Error {
  constructor(readonly code: number, message: string, readonly data?: unknown) {
    super(message)
    this.name = 'ContainerMethodError'
    this.data = data
  }
}
